/**
 * Data layer for {@link components/admin/CaseStudyGenerator}: existing-content
 * load, transcript-quality probe, the SSE generation trigger, and the
 * completion/recovery poll. Extracted so the component stays presentational
 * (audit item 30 / A1).
 *
 * Recipe keying (the recipes feature, Phase 1): every piece of generated content
 * is identified by the recipe that produced it (`case_studies.recipe_id`), not
 * by its content type. Two user recipes both run as content_type='custom', so
 * content_type is no longer a unique key. The Generated / in-progress / error
 * state is therefore keyed by recipe id, mirroring the
 * `coalesce(recipe_id::text, content_type)` unique index the backend writes.
 *
 * Response scoping (CA2): every read is scoped by `interview_data_id` when a
 * `responseId` is provided (`.eq`), and to campaign-level rows
 * (`interview_data_id IS NULL`, `.is`) otherwise, mirroring the generate API.
 * A session-wide read clobbers content across respondents on multi-response
 * sessions and `.single()` throws once a session has more than one row.
 *
 * Generation status (item 33 dual-read window): the server is migrating its
 * in-progress lock out of the magic `__GEN_IN_PROGRESS__:` content prefix into
 * a real `generation_status` column. Until the migration applies everywhere,
 * this hook reads the status column when present and falls back to the prefix.
 * Both signals resolve to the same {@link GenerationLockState}. Remove the
 * prefix fallback once the migration is live in every environment.
 *
 * @module components/hooks/useCaseStudies
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  generateCaseStudy,
  type ContentType,
  type CaseStudyResponse,
} from '../../services/geminiService';
import { useSupabase } from '../../lib/SupabaseProvider';
import {
  captureException,
  captureEvent,
} from '../../lib/telemetry/posthog/frontend';
import { resolveRecipeErrorMessage } from '../admin/recipe-error-presentation';

/** Magic prefix the legacy lock encodes into the `content` column. */
const GENERATION_LOCK_PREFIX = '__GEN_IN_PROGRESS__:';

/**
 * How long the navigation-recovery poll waits before giving up and surfacing a
 * "try again" error. The generation pipeline itself estimates ~3 minutes
 * (see {@link components/admin/CaseStudyGenerator}'s progress UI), so this must
 * sit comfortably above that estimate. At the old 3-minute value the poll fired
 * exactly when a legitimate run was still completing, disabling "Try Again"
 * mid-generation. Five minutes gives the pipeline its full estimate plus
 * headroom for network and cold-start latency.
 */
export const RECOVERY_POLL_TIMEOUT_MS = 5 * 60 * 1000;

/** Match the server's generation lock lease before offering an abandoned run again. */
const GENERATION_LOCK_TTL_MS = 10 * 60 * 1000;

/** Whether the persisted worker has reported progress within its lease. */
function isGenerationActive(lock: GenerationLockState): boolean {
  return Date.now() - (lock.lastHeartbeat || 0) < GENERATION_LOCK_TTL_MS;
}

/** Decoded server-side generation lock state. */
export interface GenerationLockState {
  /** Durable jobs remain active while their server lease is fresh. */
  jobId?: string;
  startedAt: number;
  lastHeartbeat: number;
  stage: string;
  contentType: ContentType;
}

/** Transcript sufficiency probe for the active response. */
export interface TranscriptQuality {
  userTurnCount: number;
  userWordCount: number;
  meetsThreshold: boolean;
}

/** A `case_studies` row shape this hook reads (status column may be absent
 *  until the migration lands). */
interface CaseStudyRow {
  id?: string;
  content?: string | null;
  content_type?: string | null;
  recipe_id?: string | null;
  generation_status?: string | null;
  generation_state?: (GenerationLockState & { errorCode?: string }) | null;
}

/**
 * Resolve the generation lock for a row across the dual-read window. Prefers
 * the `generation_status` column (`in_progress` + `generation_state`) and
 * falls back to parsing the legacy `__GEN_IN_PROGRESS__:` content prefix.
 * Returns null when the row is idle/complete or has no usable lock.
 */
export function parseGenerationLock(
  row: CaseStudyRow | null | undefined,
): GenerationLockState | null {
  if (!row) return null;

  // New path: the status column is the source of truth once written.
  if (row.generation_status === 'in_progress' && row.generation_state) {
    return row.generation_state;
  }
  if (
    row.generation_status === 'complete' ||
    row.generation_status === 'failed' ||
    row.generation_status === 'idle'
  ) {
    return null;
  }

  // Legacy fallback: lock encoded in the content column.
  const content = row.content;
  if (!content || !content.startsWith(GENERATION_LOCK_PREFIX)) return null;
  try {
    return JSON.parse(
      content.slice(GENERATION_LOCK_PREFIX.length),
    ) as GenerationLockState;
  } catch {
    return null;
  }
}

/** Legacy completed rows may lack a status. Locks and unfinished writes never supply readable HTML. */
function completedContent(row: CaseStudyRow): string | null {
  if (row.generation_status != null && row.generation_status !== 'complete') return null;
  return row.content?.trim() && !row.content.startsWith(GENERATION_LOCK_PREFIX) ? row.content : null;
}

/** Only a terminal rejection on this job can supply a saved quality message. */
function qualityFailure(row: CaseStudyRow | null | undefined): string | undefined {
  if (row?.generation_status === 'failed' && row.generation_state?.errorCode === 'output_not_reliable') {
    return resolveRecipeErrorMessage('output_not_reliable');
  }
  return undefined;
}

/** A recipe the gallery can run, reduced to what generation needs. */
export interface RecipeRef {
  /** The recipe row id (becomes `case_studies.recipe_id`). */
  id: string;
  /** Pipeline content type derived from the recipe (a built-in slug, or 'custom'). */
  contentType: ContentType;
}

/** Inputs for {@link useCaseStudies}. */
export interface UseCaseStudiesArgs {
  /** Interview session id (campaign). */
  interviewId: string;
  /** False for campaign recipe configuration, which has no response to load. */
  enabled?: boolean;
  /** Specific response (`interview_data.id`) being viewed, or undefined for
   *  campaign-level content. */
  responseId?: string;
}

/** Return contract of {@link useCaseStudies}. */
export interface UseCaseStudiesResult {
  /** Generated content keyed by recipe id (absent until generated). */
  existingContent: Record<string, string | null>;
  /** In-progress lock keyed by recipe id (absent when idle/complete). */
  inProgressByRecipe: Record<string, GenerationLockState | null>;
  /** Whether the initial content/quality load is in flight. */
  loading: boolean;
  /** Whether a generation (SSE or recovery poll) is currently running. */
  generating: boolean;
  /** A running job belongs to this recipe even when its reader is closed. */
  generatingRecipeId: string | null;
  /** Latest pipeline stage from the SSE stream, or null. */
  currentStage: string | null;
  /** Count of quality-gate revisions during the current run. */
  revisionCount: number;
  /** Error message keyed by recipe id (absent when none). */
  errorByRecipe: Record<string, string | null>;
  /** Transcript sufficiency probe, or null until it loads. */
  transcriptQuality: TranscriptQuality | null;
  /** Currently selected recipe id, or null (gallery view). */
  selectedRecipeId: string | null;
  /** Set the selected recipe id. */
  setSelectedRecipeId: (id: string | null) => void;
  /**
   * Kick off generation for a recipe (SSE + recovery poll). Pass
   * `{ force: true }` to regenerate over existing content instead of returning
   * the stored row.
   */
  handleGenerate: (recipe: RecipeRef, opts?: { force?: boolean }) => Promise<void>;
}

/**
 * Owns the CaseStudyGenerator data layer. See the module docs for recipe keying
 * and response scoping.
 */
export function useCaseStudies({
  interviewId,
  enabled = true,
  responseId,
}: UseCaseStudiesArgs): UseCaseStudiesResult {
  const { session: authSession, supabase } = useSupabase();

  const [selectedRecipeId, setSelectedRecipeId] = useState<string | null>(null);
  const [currentStage, setCurrentStage] = useState<string | null>(null);
  const [revisionCount, setRevisionCount] = useState(0);
  const [streamActive, setStreamActive] = useState(false);
  const [existingContent, setExistingContent] = useState<
    Record<string, string | null>
  >({});
  const [inProgressByRecipe, setInProgressByRecipe] = useState<
    Record<string, GenerationLockState | null>
  >({});
  const recoveryLocksRef = useRef(inProgressByRecipe);
  useEffect(() => {
    recoveryLocksRef.current = inProgressByRecipe;
  }, [inProgressByRecipe]);

  const [loading, setLoading] = useState(true);
  const [requestedRecipeId, setRequestedRecipeId] = useState<string | null>(null);
  const generatingRecipeId = requestedRecipeId ?? Object.keys(inProgressByRecipe).find(id => inProgressByRecipe[id]) ?? null;
  const [errorByRecipe, setErrorByRecipe] = useState<
    Record<string, string | null>
  >({});
  const [transcriptQuality, setTranscriptQuality] =
    useState<TranscriptQuality | null>(null);

  // Fetch all existing content on mount (scoped per CA2, keyed by recipe id).
  const fetchExistingContent = useCallback(async () => {
    if (!supabase) return;

    setLoading(true);
    try {
      let query = supabase
        .from('case_studies_visible')
        .select('content, content_type, recipe_id, generation_status, generation_state')
        .eq('interview_session_id', interviewId);
      query = responseId
        ? query.eq('interview_data_id', responseId)
        : query.is('interview_data_id', null);
      const { data } = await query;

      if (data) {
        const contentMap: Record<string, string | null> = {};
        const progressMap: Record<string, GenerationLockState | null> = {};
        const errors: Record<string, string | null> = {};
        for (const row of data as CaseStudyRow[]) {
          // Rows are keyed by recipe id; the migration backfilled it onto every
          // legacy row, so an unkeyed row is not part of the recipe gallery.
          const key = row.recipe_id;
          if (!key) continue;
          const lock = parseGenerationLock(row);
          if (lock) {
            if (isGenerationActive(lock)) {
              progressMap[key] = lock;
            } else {
              errors[key] = 'Generation was interrupted. Please try again.';
            }
          } else {
            const content = completedContent(row);
            if (content) contentMap[key] = content;
            const failure = qualityFailure(row);
            if (failure) errors[key] = failure;
          }
        }
        setExistingContent(contentMap);
        setInProgressByRecipe(progressMap);
        setErrorByRecipe(errors);

        const loadedRecipes = Object.keys(contentMap);
        if (loadedRecipes.length > 0) {
          captureEvent('content_loaded', {
            interview_id: interviewId,
            recipe_ids: loadedRecipes,
          });
        }
      }
    } catch (err) {
      if (err instanceof Error) {
        captureException(err, 'CaseStudyGenerator.fetchExistingContent', {
          interview_id: interviewId,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [interviewId, responseId, supabase]);

  // Probe transcript quality for the active response.
  const fetchTranscriptQuality = useCallback(async () => {
    if (!supabase) return;

    try {
      const query = supabase
        .from('interview_data')
        .select('conversation_turns');

      const { data } = responseId
        ? await query.eq('id', responseId).maybeSingle()
        : await query
            .eq('interview_session_id', interviewId)
            .order('response_number', { ascending: true })
            .limit(1)
            .maybeSingle();

      if (data?.conversation_turns) {
        const turns = data.conversation_turns as Array<{
          speaker: string;
          text: string;
        }>;
        const userTurns = turns.filter((turn) => turn.speaker === 'user');
        const userTurnCount = userTurns.length;
        const userWordCount = userTurns.reduce(
          (count, turn) =>
            count + turn.text.split(/\s+/).filter((w) => w.length > 0).length,
          0,
        );

        setTranscriptQuality({
          userTurnCount,
          userWordCount,
          meetsThreshold: userTurnCount >= 1,
        });
      }
    } catch (err) {
      captureException(
        err instanceof Error
          ? err
          : new Error('Failed to fetch transcript quality'),
        'CaseStudyGenerator.fetchTranscriptQuality',
        { interview_id: interviewId, response_id: responseId },
      );
    }
  }, [interviewId, responseId, supabase]);

  // Initial data fetch.
  useEffect(() => {
    if (!enabled) { setLoading(false); return; }
    void fetchExistingContent();
    void fetchTranscriptQuality();
  }, [enabled, fetchExistingContent, fetchTranscriptQuality]);

  // Completion / stage-recovery poll. Skipped while the SSE stream is active.
  useEffect(() => {
    if (!supabase || !generatingRecipeId) return undefined;
    if (streamActive) return undefined;

    const recipeId = generatingRecipeId;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;
    let latestLock: GenerationLockState | null = recoveryLocksRef.current[recipeId] ?? null;

    /** Clear both the progress display and the lock that disables Generate. */
    const stopRecovery = (error?: string) => {
      stopped = true;
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
      setRequestedRecipeId(null);
      setCurrentStage(null);
      setInProgressByRecipe((prev) => ({ ...prev, [recipeId]: null }));
      setErrorByRecipe((prev) => ({ ...prev, [recipeId]: error ?? null }));
    };

    const pollForCompletion = async () => {
      let query = supabase
        .from('case_studies_visible')
        .select('id, content, content_type, recipe_id, generation_status, generation_state')
        .eq('interview_session_id', interviewId)
        .eq('recipe_id', recipeId);
      query = responseId
        ? query.eq('interview_data_id', responseId)
        : query.is('interview_data_id', null);
      const { data, error } = await query.maybeSingle();
      if (stopped) return;
      if (error) {
        captureException(error, 'CaseStudyGenerator.pollForCompletion', {
          interview_id: interviewId, response_id: responseId, recipe_id: recipeId,
        });
        if (latestLock && !isGenerationActive(latestLock)) {
          stopRecovery('Generation was interrupted. Please try again.');
        }
        return;
      }

      const lock = parseGenerationLock(data as CaseStudyRow | null);
      if (lock) {
        latestLock = lock;
        if (!isGenerationActive(lock)) {
          stopRecovery('Generation was interrupted. Please try again.');
          return;
        }
        setCurrentStage(lock.stage || null);
        setInProgressByRecipe((prev) => ({ ...prev, [recipeId]: lock }));
        return;
      }

      const content = data && completedContent(data);
      if (!content) {
        stopRecovery(qualityFailure(data) ?? 'Generation is no longer active. Please generate again.');
        return;
      }

      setExistingContent((prev) => ({ ...prev, [recipeId]: content }));
      stopRecovery();

    };

    intervalId = setInterval(pollForCompletion, 3000);

    // Navigation recovery polling timeout. Held above the pipeline's ~3 minute
    // estimate so a still-running generation is never cut off early.
    timeoutId = setTimeout(() => {
      if (latestLock?.jobId && isGenerationActive(latestLock)) return;
      stopRecovery('Generation timed out. Please try again.');
    }, RECOVERY_POLL_TIMEOUT_MS);

    return () => {
      stopped = true;
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [generatingRecipeId, streamActive, interviewId, responseId, supabase]);

  const handleGenerate = useCallback(
    async (recipe: RecipeRef, opts?: { force?: boolean }) => {
      if (!authSession?.access_token) {
        setErrorByRecipe((prev) => ({
          ...prev,
          [recipe.id]: 'Please sign in to generate content',
        }));
        return;
      }

      setSelectedRecipeId(recipe.id);
      setRequestedRecipeId(recipe.id);
      setCurrentStage(null);
      setRevisionCount(0);
      setErrorByRecipe((prev) => ({ ...prev, [recipe.id]: null }));
      setStreamActive(true);
      let keepGeneratingAfterReturn = false;

      captureEvent('content_generation_started', {
        interview_id: interviewId,
        recipe_id: recipe.id,
        content_type: recipe.contentType,
      });

      try {
        const result: CaseStudyResponse = await generateCaseStudy(
          interviewId,
          authSession.access_token,
          recipe.contentType,
          (stage) => {
            setCurrentStage(stage);
            if (stage === 'revising') {
              setRevisionCount((prev) => prev + 1);
            }
          },
          responseId,
          recipe.id,
          opts?.force,
        );

        if (result.in_progress) {
          keepGeneratingAfterReturn = true;
          setCurrentStage(result.generation_state?.stage || null);
          setInProgressByRecipe((prev) => ({
            ...prev,
            [recipe.id]: result.generation_state || {
              startedAt: Date.now(),
              lastHeartbeat: Date.now(),
              stage: 'queued',
              contentType: recipe.contentType,
            },
          }));
          setStreamActive(false);
          return;
        }

        if (result.success && result.content) {
          setErrorByRecipe((prev) => ({ ...prev, [recipe.id]: null }));
          setExistingContent((prev) => ({
            ...prev,
            [recipe.id]: result.content!,
          }));
          setInProgressByRecipe((prev) => ({ ...prev, [recipe.id]: null }));
          captureEvent('content_generated', {
            interview_id: interviewId,
            recipe_id: recipe.id,
            content_type: recipe.contentType,
            case_study_id: result.case_study_id,
            content_length: result.content.length,
          });
        } else {
          const errorMessage = resolveRecipeErrorMessage(result.error_code);
          setErrorByRecipe((prev) => ({
            ...prev,
            [recipe.id]: errorMessage,
          }));
          if (
            result.error_code !== 'transcript_empty' &&
            result.error_code !== 'output_not_reliable'
          ) {
            captureException(
              new Error(result.error || errorMessage),
              'CaseStudyGenerator.handleGenerate',
              { interview_id: interviewId, recipe_id: recipe.id },
            );
          }
        }
      } catch (err) {
        const errorMessage = resolveRecipeErrorMessage();
        setErrorByRecipe((prev) => ({ ...prev, [recipe.id]: errorMessage }));
        captureException(
          err instanceof Error ? err : new Error(errorMessage),
          'CaseStudyGenerator.handleGenerate',
          { interview_id: interviewId, recipe_id: recipe.id },
        );
      } finally {
        setStreamActive(false);
        if (!keepGeneratingAfterReturn) {
          setInProgressByRecipe((prev) => ({ ...prev, [recipe.id]: null }));
          setRequestedRecipeId(null);
          setCurrentStage(null);
          setRevisionCount(0);
        }
      }
    },
    [authSession?.access_token, interviewId, responseId],
  );

  return {
    existingContent,
    inProgressByRecipe,
    loading,
    generating: !!generatingRecipeId,
    generatingRecipeId,
    currentStage: currentStage ?? (generatingRecipeId && inProgressByRecipe[generatingRecipeId]?.stage) ?? null,
    revisionCount,
    errorByRecipe,
    transcriptQuality,
    selectedRecipeId,
    setSelectedRecipeId,
    handleGenerate,
  };
}
